/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, getCatalogId, IAction, IActionContext, IActionInput, ICommerceApiSettings, ICreateActionContext, IObservableAction, IRequestContext } from '@msdyn365-commerce/core';

import { createGlobalState, GlobalStateConstructor, IGlobalState } from '../global-state/i-global-state';

export declare type GlobalStateType = 'CARTSTATE' | 'CHECKOUTSTATE';

/**
 * Shared class that handles global state inputs.
 */
export class GlobalStateInput<TOut extends IGlobalState> implements IActionInput {
    public readonly stateConstructor: GlobalStateConstructor<TOut>;

    public readonly key: GlobalStateType;

    public readonly catalogId?: number;

    private readonly apiSettings: ICommerceApiSettings;

    public constructor(
        key: GlobalStateType,
        stateConstructor: GlobalStateConstructor<TOut>,
        apiSettings: ICommerceApiSettings,
        requestContext?: IRequestContext
    ) {
        this.apiSettings = apiSettings;
        this.stateConstructor = stateConstructor;
        this.key = key;

        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    public getCacheKey = () => `${this.key}-chanId:${this.apiSettings.channelId}-catId:${this.catalogId ?? 0}`;

    public getCacheObjectType = () => this.key;

    public dataCacheType = (): CacheType => 'instance';
}

export async function getState<TOut extends IGlobalState>(input: GlobalStateInput<TOut>, ctx: IActionContext): Promise<TOut> {
    const globalStateObject = createGlobalState(input.stateConstructor, ctx);

    await globalStateObject.initialize();

    return globalStateObject;
}

export default function createGlobalStateDataAction<TOut extends IGlobalState>(key: GlobalStateType, stateConstructor: GlobalStateConstructor<TOut>): IObservableAction<TOut> {
    return createObservableDataAction({
        action: <IAction<TOut>>getState,
        input: (args: ICreateActionContext) => {
            return new GlobalStateInput(key, stateConstructor, args.requestContext.apiSettings, args.requestContext);
        }
    });
}
